import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import TaskEmbed from "./TaskEmbed";
import QuizEmbed from "./QuizEmbed";
import IntegrationActionButton from "./IntegrationActionButton";
import IntegrationCredentials from "./IntegrationCredentials";
import IntegrationVerify from "./IntegrationVerify";
import "./TaskBasedLesson.css";
import axios from "axios";

// Shared ReactMarkdown renderers (code blocks + external links). Hoisted so both
// the default lesson body and the integration step-card renderer use the same
// markdown treatment.
const MARKDOWN_COMPONENTS = {
  code({ node, inline, className, children, ...props }) {
    const match = /language-(\w+)/.exec(className || "");
    return !inline && match ? (
      <SyntaxHighlighter
        children={String(children).replace(/\n$/, "")}
        style={vscDarkPlus}
        language={match[1]}
        PreTag="div"
        {...props}
      />
    ) : (
      <code className={className} {...props}>
        {children}
      </code>
    );
  },
  a({ node, children, href, ...props }) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
        {children}
      </a>
    );
  },
};

// For integration lessons, group the parsed parts into H2 (`## `) sections so an
// interactive widget (install / credentials / verify) can be wrapped together
// with the heading it belongs under — making it obvious which button answers
// which ask. Only sections that actually contain an embed get boxed (`hasEmbed`);
// pure-prose teaching sections render plainly, and the heading text stays inside
// the markdown so it renders as a normal H2. Content before the first heading is
// returned as `lead` (always plain).
function buildIntegrationSections(parts) {
  const lead = [];
  const sections = [];
  let current = null;

  const pushMd = (text) => {
    if (!text.trim()) return;
    (current ? current.tokens : lead).push({ kind: "md", content: text });
  };
  const pushEmbed = (part) => {
    if (current) {
      current.tokens.push({ kind: "embed", part });
      current.hasEmbed = true;
    } else {
      lead.push({ kind: "embed", part });
    }
  };

  for (const part of parts) {
    if (part.type !== "markdown") {
      pushEmbed(part);
      continue;
    }

    let buffer = [];
    let inFence = false;
    const flush = () => {
      pushMd(buffer.join("\n"));
      buffer = [];
    };

    for (const line of part.content.split("\n")) {
      const trimmed = line.trimStart();
      if (trimmed.startsWith("```") || trimmed.startsWith("~~~")) {
        inFence = !inFence;
        buffer.push(line);
        continue;
      }
      if (!inFence && /^##\s+/.test(line)) {
        flush(); // close out prose under the previous section (or lead)
        current = { tokens: [], hasEmbed: false };
        sections.push(current);
        buffer.push(line); // keep the heading text so it renders as a normal H2
      } else {
        buffer.push(line);
      }
    }
    flush();
  }

  return { lead, sections };
}

function TaskBasedLesson({ challenge, nextLessonSlug, previousLessonSlug }) {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  // Integration projects (Course.renderType === "integration") verify by clicking a
  // button, not by writing code — so we render an inline Verify card and drop the
  // right-hand Tasks panel / code editor entirely.
  const isIntegration = challenge?.courseDetails?.renderType === "integration";
  const [isCompleting, setIsCompleting] = useState(false);
  const [activeTaskSlug, setActiveTaskSlug] = useState(null);
  const [activeQuizSlug, setActiveQuizSlug] = useState(null);
  const [taskProgressMap, setTaskProgressMap] = useState({});
  const [quizProgressMap, setQuizProgressMap] = useState({});
  const [panelWidth, setPanelWidth] = useState(600);
  const [isDragging, setIsDragging] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedLesson, setEditedLesson] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Parse markdown and extract task and quiz references
  const parsedContent = useMemo(() => {
    if (!challenge?.lesson) return { parts: [], tasks: [], quizzes: [] };

    const markdown = challenge.lesson;
    const parts = [];
    const tasks = [];
    const quizzes = [];

    // Combined regex: {{task:slug}}, {{quiz:slug}}, {{install-data:key}} (all take an
    // arg) OR {{install-schema}} / {{credentials}} (no arg). The two shapes are
    // captured in different groups: group 1+2 for the arg form, group 3 for the bare form.
    const regex = /\{\{(task|quiz|install-data):([a-z0-9-]+)\}\}|\{\{(install-schema|credentials)\}\}/g;

    let lastIndex = 0;
    let match;
    let taskIndex = 1;
    let quizIndex = 1;

    while ((match = regex.exec(markdown)) !== null) {
      // Add markdown content before the match
      if (match.index > lastIndex) {
        parts.push({
          type: "markdown",
          content: markdown.slice(lastIndex, match.index),
        });
      }

      const itemType = match[1] || match[3]; // "task" | "quiz" | "install-data" | "install-schema" | "credentials"
      const slug = match[2];

      if (itemType === "install-schema") {
        parts.push({ type: "action", action: "install-schema" });
      } else if (itemType === "install-data") {
        parts.push({ type: "action", action: "install-data", dataKey: slug });
      } else if (itemType === "credentials") {
        parts.push({ type: "action", action: "credentials" });
      } else if (itemType === "task") {
        // Add task placeholder in content
        parts.push({
          type: "task-placeholder",
          slug,
          taskNumber: taskIndex,
        });

        // Store task reference for sidebar
        tasks.push({
          slug,
          taskNumber: taskIndex,
        });

        taskIndex++;
      } else if (itemType === "quiz") {
        // Add quiz placeholder in content
        parts.push({
          type: "quiz-placeholder",
          slug,
          quizNumber: quizIndex,
        });

        // Store quiz reference for sidebar
        quizzes.push({
          slug,
          quizNumber: quizIndex,
        });

        quizIndex++;
      }

      lastIndex = match.index + match[0].length;
    }

    // Add remaining markdown after last match
    if (lastIndex < markdown.length) {
      parts.push({
        type: "markdown",
        content: markdown.slice(lastIndex),
      });
    }

    return { parts, tasks, quizzes };
  }, [challenge?.lesson]);

  const handleNextLesson = async () => {
    if (!nextLessonSlug) return;

    setIsCompleting(true);
    try {
      // Navigate to next lesson if available
      // Note: Challenge completion is now handled automatically on the server
      // when all associated tasks are completed
      navigate(`/lesson/${nextLessonSlug}`);
    } catch (err) {
      console.error("Error navigating to next lesson:", err);
    } finally {
      setIsCompleting(false);
    }
  };

  const handlePreviousLesson = () => {
    if (!previousLessonSlug) return;
    navigate(`/lesson/${previousLessonSlug}`);
  };

  const handleTaskClick = (taskSlug) => {
    setActiveTaskSlug(activeTaskSlug === taskSlug ? null : taskSlug);
    // Close any open quiz when opening a task
    if (activeTaskSlug !== taskSlug) {
      setActiveQuizSlug(null);
    }
  };

  const handleQuizClick = (quizSlug) => {
    setActiveQuizSlug(activeQuizSlug === quizSlug ? null : quizSlug);
    // Close any open task when opening a quiz
    if (activeQuizSlug !== quizSlug) {
      setActiveTaskSlug(null);
    }
  };

  const handleTaskProgressUpdate = (taskSlug, progress) => {
    setTaskProgressMap(prev => ({
      ...prev,
      [taskSlug]: progress
    }));
  };

  const handleQuizProgressUpdate = (quizSlug, progress) => {
    setQuizProgressMap(prev => ({
      ...prev,
      [quizSlug]: progress
    }));
  };

  // Calculate overall progress (tasks + quizzes)
  const overallProgress = useMemo(() => {
    const taskProgressValues = Object.values(taskProgressMap);
    const quizProgressValues = Object.values(quizProgressMap);

    const completedTasks = taskProgressValues.filter(p => p?.status === "Completed").length;
    const completedQuizzes = quizProgressValues.filter(p => p?.status === "Completed").length;

    const totalTasks = parsedContent.tasks.length;
    const totalQuizzes = parsedContent.quizzes.length;

    return {
      completed: completedTasks + completedQuizzes,
      total: totalTasks + totalQuizzes,
      completedTasks,
      totalTasks,
      completedQuizzes,
      totalQuizzes,
    };
  }, [taskProgressMap, quizProgressMap, parsedContent.tasks.length, parsedContent.quizzes.length]);

  // Integration lessons: group parts into H2 sections so each interactive widget
  // sits in the same box as its heading (prose-only sections stay unboxed).
  const integrationSections = useMemo(
    () => (isIntegration ? buildIntegrationSections(parsedContent.parts) : null),
    [isIntegration, parsedContent.parts]
  );

  // Handle divider dragging
  const handleMouseDown = (e) => {
    setIsDragging(true);
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const newWidth = window.innerWidth - e.clientX;
      if (newWidth >= 400 && newWidth <= 1000) {
        setPanelWidth(newWidth);
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  // Initialize edited lesson content when entering edit mode
  useEffect(() => {
    if (isEditMode && challenge?.lesson) {
      setEditedLesson(challenge.lesson);
    }
  }, [isEditMode, challenge?.lesson]);

  // Check if user is admin
  const isAdmin = user?.[0]?.isAdmin || false;

  const handleToggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  const handleSaveLesson = async () => {
    if (!challenge?._id) return;

    setIsSaving(true);
    try {
      await axios.put(`/api/lesson/${challenge._id}/update`, {
        lesson: editedLesson,
      });

      // Refresh the page to show updated content
      window.location.reload();
    } catch (error) {
      console.error("Error saving lesson:", error);
      alert("Failed to save lesson. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedLesson(challenge?.lesson || "");
  };

  // --- Integration lesson rendering ----------------------------------------
  // Prose flows plainly; a section that contains an interactive widget is boxed
  // together with its heading so the button is clearly tied to its ask.
  const renderIntegrationMarkdown = (content, key) => (
    <div key={key} className="task-lesson__markdown">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={MARKDOWN_COMPONENTS}
      >
        {content}
      </ReactMarkdown>
    </div>
  );

  const renderIntegrationEmbed = (part, key) => {
    if (part.type === "action") {
      if (part.action === "credentials") return <IntegrationCredentials key={key} />;
      if (part.action === "install-schema") {
        return <IntegrationActionButton key={key} action="install-schema" label="Install Order Schema" />;
      }
      if (part.action === "install-data") {
        return (
          <IntegrationActionButton
            key={key}
            action="install-data"
            dataKey={part.dataKey}
            label="Install Sample Data"
          />
        );
      }
      return null;
    }
    if (part.type === "task-placeholder") {
      return (
        <IntegrationVerify
          key={key}
          taskSlug={part.slug}
          taskNumber={part.taskNumber}
          onProgressUpdate={handleTaskProgressUpdate}
        />
      );
    }
    return null;
  };

  const renderIntegrationParts = () => {
    const { lead, sections } = integrationSections || { lead: [], sections: [] };
    let k = 0;
    const renderToken = (tok) =>
      tok.kind === "md"
        ? renderIntegrationMarkdown(tok.content, `int-md-${k++}`)
        : renderIntegrationEmbed(tok.part, `int-embed-${k++}`);

    return (
      <>
        {lead.map(renderToken)}
        {sections.map((section, si) =>
          section.hasEmbed ? (
            <section className="int-task-group" key={`int-group-${si}`}>
              {section.tokens.map(renderToken)}
            </section>
          ) : (
            section.tokens.map(renderToken)
          )
        )}
      </>
    );
  };

  return (
    <div className={`task-based-lesson${isIntegration ? " task-based-lesson--integration" : ""}`}>
      {/* Main Content Area - Left Side */}
      <div className="task-lesson__main-panel">
        {/* Lesson Header */}
        <div className="task-lesson__header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1 className="task-lesson__title">{challenge?.name}</h1>
          </div>
          {isAdmin && (
            <div style={{ display: 'flex', gap: '8px' }}>
              {isEditMode ? (
                <>
                  <button
                    onClick={handleSaveLesson}
                    disabled={isSaving}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: isSaving ? 'not-allowed' : 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                      opacity: isSaving ? 0.6 : 1,
                    }}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: isSaving ? 'not-allowed' : 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                    }}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={handleToggleEditMode}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                  }}
                >
                  Edit Lesson
                </button>
              )}
            </div>
          )}
        </div>

        {/* Scrollable Lesson Content */}
        <div className="task-lesson__content">
          {isEditMode ? (
            <div style={{ padding: '20px', height: '100%' }}>
              <textarea
                value={editedLesson}
                onChange={(e) => setEditedLesson(e.target.value)}
                style={{
                  width: '100%',
                  height: '100%',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  padding: '16px',
                  border: '1px solid rgba(113, 113, 122, 0.3)',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(24, 24, 27, 0.6)',
                  color: '#fff',
                  resize: 'none',
                  outline: 'none',
                }}
              />
            </div>
          ) : (
            <div className={`task-lesson__content-inner${isIntegration ? " task-lesson__content-inner--integration" : ""}`}>
              {isIntegration ? renderIntegrationParts() : parsedContent.parts.map((part, index) => {
              if (part.type === "action") {
                if (part.action === "credentials") {
                  return <IntegrationCredentials key={`action-credentials-${index}`} />;
                }
                if (part.action === "install-schema") {
                  return (
                    <IntegrationActionButton
                      key={`action-schema-${index}`}
                      action="install-schema"
                      label="Install Order Schema"
                    />
                  );
                }
                if (part.action === "install-data") {
                  return (
                    <IntegrationActionButton
                      key={`action-data-${index}`}
                      action="install-data"
                      dataKey={part.dataKey}
                      label="Install Sample Data"
                    />
                  );
                }
                return null;
              } else if (part.type === "markdown") {
                return (
                  <div key={`md-${index}`} className="task-lesson__markdown">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                      components={MARKDOWN_COMPONENTS}
                    >
                      {part.content}
                    </ReactMarkdown>
                  </div>
                );
              } else if (part.type === "task-placeholder") {
                // Integration lessons: inline Verify button, no editor / no sidebar.
                if (isIntegration) {
                  return (
                    <IntegrationVerify
                      key={`verify-${part.slug}`}
                      taskSlug={part.slug}
                      taskNumber={part.taskNumber}
                      onProgressUpdate={handleTaskProgressUpdate}
                    />
                  );
                }

                const taskProgress = taskProgressMap[part.slug];
                const isTaskCompleted = taskProgress?.status === "Completed";

                return (
                  <div
                    key={`task-placeholder-${part.slug}`}
                    className={`task-lesson__task-placeholder ${
                      isTaskCompleted
                        ? 'task-lesson__task-placeholder--complete'
                        : 'task-lesson__task-placeholder--incomplete'
                    }`}
                    onClick={() => handleTaskClick(part.slug)}
                  >
                    <div className="task-lesson__task-content">
                      <div className="task-lesson__task-info">
                        <div className="task-lesson__task-header">
                          <h3 className="task-lesson__task-title">
                            Task {part.taskNumber}
                          </h3>
                          {isTaskCompleted && (
                            <span className="task-lesson__task-check">✓ Completed</span>
                          )}
                        </div>
                        <p className="task-lesson__task-description">
                          {isTaskCompleted
                            ? "Task completed! Click to review →"
                            : "Click to open task in the right panel →"}
                        </p>
                        {taskProgress?.attemptCounter > 0 && (
                          <p className="task-lesson__task-attempts">
                            {taskProgress.attemptCounter} attempt{taskProgress.attemptCounter !== 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                      <div className={`task-lesson__task-button ${
                        isTaskCompleted
                          ? 'task-lesson__task-button--complete'
                          : 'task-lesson__task-button--incomplete'
                      }`}>
                        {isTaskCompleted ? '✓ Completed' : 'Start Task'}
                      </div>
                    </div>
                  </div>
                );
              } else if (part.type === "quiz-placeholder") {
                const quizProgress = quizProgressMap[part.slug];
                const isQuizCompleted = quizProgress?.status === "Completed";

                return (
                  <div
                    key={`quiz-placeholder-${part.slug}`}
                    className={`task-lesson__quiz-placeholder ${
                      isQuizCompleted
                        ? 'task-lesson__quiz-placeholder--complete'
                        : 'task-lesson__quiz-placeholder--incomplete'
                    }`}
                    onClick={() => handleQuizClick(part.slug)}
                  >
                    <div className="task-lesson__quiz-content">
                      <div className="task-lesson__quiz-info">
                        <div className="task-lesson__quiz-header">
                          <h3 className="task-lesson__quiz-title">
                            Quiz {part.quizNumber}
                          </h3>
                          {isQuizCompleted && (
                            <span className="task-lesson__quiz-check">✓ Passed</span>
                          )}
                        </div>
                        <p className="task-lesson__quiz-description">
                          {isQuizCompleted
                            ? "Quiz passed! Click to review →"
                            : "Click to take quiz in the right panel →"}
                        </p>
                        {quizProgress?.attemptCounter > 0 && (
                          <p className="task-lesson__quiz-attempts">
                            {quizProgress.attemptCounter} attempt{quizProgress.attemptCounter !== 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                      <div className={`task-lesson__quiz-button ${
                        isQuizCompleted
                          ? 'task-lesson__quiz-button--complete'
                          : 'task-lesson__quiz-button--incomplete'
                      }`}>
                        {isQuizCompleted ? '✓ Passed' : 'Start Quiz'}
                      </div>
                    </div>
                  </div>
                );
              }
              return null;
            })}
            </div>
          )}

          {/* Navigation */}
          {!isEditMode && (
            <div className="task-lesson__navigation">
            {previousLessonSlug && (
              <button
                onClick={handlePreviousLesson}
                className="task-lesson__previous-button"
              >
                ← Previous Lesson
              </button>
            )}
            {nextLessonSlug && (
              <button
                onClick={handleNextLesson}
                disabled={isCompleting}
                className="task-lesson__next-button"
              >
                {isCompleting ? "Completing..." : "Next Lesson →"}
              </button>
            )}
            </div>
          )}
        </div>
      </div>

      {!isIntegration && (
      <>
      {/* Resizable Divider */}
      <div
        className={`task-lesson__divider ${isDragging ? 'task-lesson__divider--dragging' : ''}`}
        onMouseDown={handleMouseDown}
      >
        <div className="task-lesson__divider-hitbox" />
      </div>

      {/* Right Side Panel - Tasks & Quizzes */}
      <div
        className="task-lesson__sidebar"
        style={{ width: `${panelWidth}px` }}
      >
        <div className="task-lesson__sidebar-header">
          <div className="task-lesson__sidebar-header-top">
            <h2 className="task-lesson__sidebar-title">
              {parsedContent.quizzes.length > 0 ? "Tasks & Quizzes" : "Tasks"}
            </h2>
            {overallProgress.total > 0 && (
              <div className="task-lesson__progress">
                <div className="task-lesson__progress-text">
                  <span className="task-lesson__progress-completed">{overallProgress.completed}</span>
                  <span className="task-lesson__progress-total">/{overallProgress.total}</span>
                </div>
                <div className="task-lesson__progress-bar">
                  <div
                    className="task-lesson__progress-fill"
                    style={{
                      width: `${(overallProgress.completed / overallProgress.total) * 100}%`
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          <p className="task-lesson__sidebar-subtitle">
            {parsedContent.tasks.length > 0 && (
              <span>{parsedContent.tasks.length} task{parsedContent.tasks.length !== 1 ? 's' : ''}</span>
            )}
            {parsedContent.tasks.length > 0 && parsedContent.quizzes.length > 0 && (
              <span>, </span>
            )}
            {parsedContent.quizzes.length > 0 && (
              <span>{parsedContent.quizzes.length} quiz{parsedContent.quizzes.length !== 1 ? 'zes' : ''}</span>
            )}
            <span> in this lesson</span>
          </p>
        </div>

        <div className="task-lesson__sidebar-content">
          {/* Render Tasks */}
          {parsedContent.tasks.map((task) => (
            <TaskEmbed
              key={`task-${task.slug}`}
              taskSlug={task.slug}
              taskNumber={task.taskNumber}
              isActive={activeTaskSlug === task.slug}
              onToggle={() => handleTaskClick(task.slug)}
              onProgressUpdate={handleTaskProgressUpdate}
            />
          ))}

          {/* Render Quizzes */}
          {parsedContent.quizzes.map((quiz) => (
            <QuizEmbed
              key={`quiz-${quiz.slug}`}
              quizSlug={quiz.slug}
              quizNumber={quiz.quizNumber}
              isActive={activeQuizSlug === quiz.slug}
              onToggle={() => handleQuizClick(quiz.slug)}
              onProgressUpdate={handleQuizProgressUpdate}
            />
          ))}
        </div>
      </div>
      </>
      )}
    </div>
  );
}

export default TaskBasedLesson;
